import React, { useState, KeyboardEvent } from 'react';
import styles from './conversationList.module.scss';
import CustomIcon from '@/components/ui/CustomIcon';
import { Conversation, formatRelativeTime } from '@/modules/chat/types/conversation';
import PrivacyPopup from '../privacyPopup/PrivacyPopup';

type Props = {
    conversations: Conversation[];
    onSelectConversation: (conversationId: string) => void;
    onDeleteConversation: (conversationId: string) => void;
    onCreateNewConversation: (firstMessage?: string) => void;
    primaryColor?: string;
    showPrivacyPopup?: boolean;
    setShowPrivacyPopup?: (show: boolean) => void;
};

const ConversationList = ({
    conversations,
    onSelectConversation,
    onDeleteConversation,
    onCreateNewConversation,
    primaryColor,
    showPrivacyPopup,
    setShowPrivacyPopup,
}: Props): React.ReactElement => {
    const [newMessage, setNewMessage] = useState('');

    const handleSubmit = (): void => {
        const message = newMessage.trim();
        if (message) {
            onCreateNewConversation(message);
            setNewMessage('');
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    const handleDelete = (e: React.MouseEvent, conversationId: string): void => {
        e.stopPropagation();
        onDeleteConversation(conversationId);
    };

    const containerStyle = {
        ...(primaryColor ? { '--primary-color': primaryColor } : {}),
    } as React.CSSProperties;

    return (
        <div className={styles['conversation-list']} style={containerStyle}>
            <div className={styles.header}>
                <h2 className={styles['header-title']}>Mensajes</h2>
            </div>

            <div className={styles.list}>
                {conversations.length === 0 ? (
                    <div className={styles['empty-state']}>
                        <div className={styles['empty-icon']}>
                            <CustomIcon name="assistantFace" width={48} height={48} />
                        </div>
                        <p className={styles['empty-text']}>No tienes conversaciones anteriores</p>
                    </div>
                ) : (
                    conversations.map(conversation => (
                        <div
                            key={conversation.id}
                            className={styles['conversation-item']}
                            onClick={() => onSelectConversation(conversation.id)}
                        >
                            <div className={styles['conversation-icon']}>
                                <CustomIcon name="assistantFace" width={24} height={24} />
                            </div>
                            <div className={styles['conversation-content']}>
                                <h3 className={styles.title}>{conversation.title}</h3>
                                <p className={styles.preview}>{conversation.lastMessagePreview}</p>
                            </div>
                            <div className={styles['conversation-meta']}>
                                <span className={styles.time}>
                                    {formatRelativeTime(conversation.timestamp)}
                                </span>
                                <button
                                    className={styles['delete-button']}
                                    onClick={(e) => handleDelete(e, conversation.id)}
                                    aria-label="Eliminar conversación"
                                >
                                    <CustomIcon name="trash" width={16} height={16} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <div className={styles['new-conversation']}>
                {showPrivacyPopup && setShowPrivacyPopup && (
                    <PrivacyPopup
                        onClose={() => setShowPrivacyPopup(false)}
                        primaryColor={primaryColor}
                    />
                )}
                <div className={styles['new-conversation-input']}>
                    <input
                        className={styles['new-conversation-text-input']}
                        type="text"
                        placeholder="Iniciar una nueva conversación..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    <button
                        className={styles['new-conversation-submit']}
                        onClick={handleSubmit}
                        disabled={!newMessage.trim()}
                        aria-label="Enviar mensaje"
                    >
                        <CustomIcon name="send" width={20} height={20} />
                    </button>
                </div>
            </div>

            <div className={styles.footer}>
                <p className={styles['footer-text']}>Contenido generado por IA.</p>
            </div>
        </div>
    );
};

export default ConversationList;
